package com.narbase.letsgo.web.views.pushpayload

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.percent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.models.pushpayload.PushDeliveryPriority
import com.narbase.letsgo.web.models.pushpayload.PushDeliveryType
import com.narbase.letsgo.web.routing.domain.admin.pushpayload.GetPushSendHistoryEndpoint
import com.narbase.letsgo.web.routing.domain.admin.pushpayload.SendPushPayloadEndpoint
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.scrollable.scrollable
import com.narbase.letsgo.web.utils.table.headerCell
import com.narbase.letsgo.web.utils.table.listTable
import com.narbase.letsgo.web.utils.table.tableCell
import com.narbase.letsgo.web.utils.table.tableRow
import com.narbase.letsgo.web.utils.verticalFiller
import com.narbase.letsgo.web.utils.verticalSeparator
import com.narbase.letsgo.web.utils.views.*
import com.narbase.letsgo.web.utils.views.customViews.customCheckBox
import com.narbase.letsgo.web.views.callCenter.PassengerSelectorDialog

class PushPayloadManagementComponent : Component() {

    private val viewModel = PushPayloadManagementViewModel()

    private var payloadInput: TextArea? = null
    private var alertTitleInput: TextInput? = null
    private var alertBodyInput: TextInput? = null
    private var expiryInput: TextInput? = null
    private var alertFieldsLayout: LinearLayout? = null
    private var recipientsTableBody: View? = null
    private var recipientsSummary: TextView? = null
    private var resultsContainer: LinearLayout? = null
    private var historyContainer: LinearLayout? = null

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        super.onViewMounted(lifecycleOwner)
        onRecipientsChanged()
        viewModel.getHistory()
    }

    override fun View?.getView() = view {
        id = "pushPayloadManagementRootView"

        style {
            matchParentDimensions
        }
        scrollable {
            style {
                matchParentDimensions
            }

            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    padding = 32.px
                }

                horizontalLayout {
                    style {
                        width = matchParent
                    }
                    textView {
                        text = "Push Payload Sender".localized()
                        style {
                            width = wrapContent
                            fontSize = 20.px
                            fontWeight = "bold"
                        }
                    }
                    horizontalFiller()
                }
                verticalFiller(20)

                payloadSection()
                verticalFiller(24)
                recipientsSection()
                verticalFiller(24)
                resultsSection()
                verticalFiller(24)
                historySection()
            }
        }
    }

    private fun LinearLayout.payloadSection() = verticalLayout {
        style {
            width = 60.percent
            height = wrapContent
        }

        theme.subTitleFit(this, "Payload".localized())

        payloadInput = theme.labeledTextArea(this, "Payload JSON".localized(), true) {
            text = viewModel.payloadJson
            style { height = 160.px }
        }
        verticalFiller(8)

        customCheckBox(
            "Send through APNs sandbox (development builds)".localized(),
            isSelected = viewModel.useSandbox,
            id = "pushPayloadUseSandboxCheckBox",
            onSelection = { viewModel.useSandbox = it },
        )
        verticalFiller(16)

        deliveryOptions()
    }

    private fun LinearLayout.deliveryOptions() {
        theme.subTitleFit(this, "Delivery".localized())
        verticalFiller(8)

        horizontalLayout {
            style {
                width = matchParent
                alignItems = Alignment.Center
            }
            setupDropDownList(
                "Push type".localized(),
                list = PushDeliveryType.values(),
                itemToString = { it.name },
                defaultValue = viewModel.deliveryType,
                onItemSelected = { selected ->
                    viewModel.deliveryType = selected ?: PushDeliveryType.BACKGROUND
                    updateAlertFieldsVisibility()
                },
            )
            horizontalFiller(16)
            setupDropDownList(
                "Priority".localized(),
                list = PushDeliveryPriority.values(),
                itemToString = { it.name },
                defaultValue = viewModel.priority,
                onItemSelected = { selected ->
                    viewModel.priority = selected ?: PushDeliveryPriority.IMMEDIATE
                },
            )
            horizontalFiller()
        }
        verticalFiller(12)

        alertFieldsLayout = verticalLayout {
            style {
                width = matchParent
                height = wrapContent
            }
            alertTitleInput = theme.labeledTextInput(this, "Alert title".localized()) {
                text = viewModel.alertTitle
            }
            verticalFiller(8)
            alertBodyInput = theme.labeledTextInput(this, "Alert body".localized()) {
                text = viewModel.alertBody
            }
            verticalFiller(8)
        }
        updateAlertFieldsVisibility()

        expiryInput = theme.labeledTextInput(this, "Expires in seconds (empty = immediate)".localized()) {
            text = viewModel.expiresInSeconds?.toString() ?: ""
            placeholder = "eg. 3600"
        }
    }

    private fun updateAlertFieldsVisibility() {
        alertFieldsLayout?.isVisible = viewModel.deliveryType == PushDeliveryType.ALERT
    }

    private fun LinearLayout.recipientsSection() = verticalLayout {
        style {
            width = matchParent
            height = wrapContent
        }

        horizontalLayout {
            style {
                width = matchParent
                alignItems = Alignment.Center
            }
            theme.subTitleFit(this, "Recipients".localized())
            horizontalFiller()
            recipientsSummary = textView {
                style {
                    width = wrapContent
                    fontSize = 14.px
                }
                text = recipientsSummaryText()
            }
            horizontalFiller(16)
            theme.unimportantButton(this) {
                id = "addPushRecipientButton"
                text = "Add passenger".localized()
                onClick = { showPassengerSelector() }
            }
            horizontalFiller(8)
            theme.unimportantButton(this) {
                text = "Clear".localized()
                onClick = {
                    viewModel.clearRecipients()
                    onRecipientsChanged()
                }
            }
            horizontalFiller(8)
            theme.mainButton(this) {
                id = "sendPushPayloadButton"
                text = "Send payload".localized()
                onClick = { onSendClicked() }
                withLoadingAndError(viewModel.sendPayloadUiState, onRetryClicked = {
                    onSendClicked()
                }, onLoaded = {
                    onSendCompleted()
                })
            }
        }
        verticalFiller(8)

        recipientsTableBody = listTable {
            headerCell("Id".localized(), 1)
            headerCell("Name".localized(), 3)
            headerCell("Phone".localized(), 3)
            headerCell("".localized(), 1)
        }
    }

    private fun showPassengerSelector() {
        PassengerSelectorDialog { extendedPassenger, _, _ ->
            val isAdded = viewModel.addRecipient(extendedPassenger)
            if (isAdded.not()) {
                SnackBar.showText("Passenger is already in the list".localized())
                return@PassengerSelectorDialog
            }
            onRecipientsChanged()
        }.show()
    }

    private fun recipientsSummaryText() =
        "${viewModel.recipientsCount} ${"selected".localized()}"

    private fun onRecipientsChanged() {
        recipientsSummary?.text = recipientsSummaryText()
        recipientsTableBody?.clearAllChildren()
        recipientsTableBody?.apply {
            if (viewModel.recipients.isEmpty()) {
                tableRow(isClickable = false) {
                    tableCell("No passengers added yet".localized(), 1, 14.px)
                }
                return@apply
            }
            viewModel.recipients.forEachIndexed { index, recipient ->
                val passenger = recipient.passenger
                tableRow(isClickable = false) {
                    tableCell("${passenger.id}", 1, 14.px)
                    tableCell(passenger.fullName, 3, 14.px)
                    tableCell(passenger.phone, 3, 14.px)
                    tableCell(1) {
                        theme.unimportantButton(this) {
                            text = "Remove".localized()
                            onClick = {
                                passenger.id?.let { viewModel.removeRecipient(it) }
                                onRecipientsChanged()
                            }
                        }
                    }
                }
                if (index != viewModel.recipients.lastIndex) {
                    verticalSeparator()
                }
            }
        }
    }

    private fun onSendClicked() {
        val payload = payloadInput?.text?.trim().orEmpty()
        if (payload.isEmpty()) {
            SnackBar.showText("Payload is empty".localized())
            return
        }
        if (viewModel.recipientsCount == 0) {
            SnackBar.showText("Add at least one passenger".localized())
            return
        }
        viewModel.payloadJson = payload
        viewModel.alertTitle = alertTitleInput?.text?.trim().orEmpty()
        viewModel.alertBody = alertBodyInput?.text?.trim().orEmpty()
        viewModel.expiresInSeconds = expiryInput?.text?.trim()?.takeIf { it.isNotEmpty() }?.toIntOrNull()

        if (viewModel.deliveryType == PushDeliveryType.ALERT && viewModel.alertTitle.isEmpty()) {
            SnackBar.showText("Alert title is required for alert pushes".localized())
            return
        }
        showRemoteConfirmationDialog(
            message = "Send this payload to ${viewModel.recipientsCount} ${"passenger(s)?".localized()}",
            actionButtonText = "Send".localized(),
            uiState = viewModel.sendPayloadUiState,
            action = { viewModel.sendPayload() },
            onConfirmed = { onSendCompleted() },
        )
    }

    private fun onSendCompleted() {
        val response = viewModel.lastSendResponse ?: return
        SnackBar.showText("Sent ${response.sentCount}, failed ${response.failedCount}")
        onResultsLoaded(response)
        viewModel.setSendPayloadUiStateToNull()
        viewModel.getHistory()
    }

    private fun LinearLayout.resultsSection() = verticalLayout {
        style {
            width = matchParent
            height = wrapContent
        }
        resultsContainer = this
    }

    private fun onResultsLoaded(response: SendPushPayloadEndpoint.Response) {
        resultsContainer?.clearAllChildren()
        resultsContainer?.apply {
            theme.subTitleFit(this, "Last send results".localized())
            verticalFiller(8)
            resultsTable(response.results.toList())
        }
    }

    private fun LinearLayout.resultsTable(results: List<SendPushPayloadEndpoint.SendResultDto>) {
        val body = listTable {
            headerCell("Id".localized(), 1)
            headerCell("Name".localized(), 2)
            headerCell("Platform".localized(), 1)
            headerCell("Result".localized(), 2)
            headerCell("Status".localized(), 1)
            headerCell("Provider id".localized(), 3)
            headerCell("Details".localized(), 2)
        }
        results.forEachIndexed { index, result ->
            body.tableRow(isClickable = false) {
                tableCell("${result.passengerId}", 1, 14.px)
                tableCell(result.fullName, 2, 14.px)
                tableCell(result.platform, 1, 14.px)
                tableCell(2) {
                    textView {
                        style {
                            width = matchParent
                            fontSize = 14.px
                            color = if (result.isSent) AppColors.greenDark else AppColors.redLight
                        }
                        text = if (result.isSent) "Accepted".localized()
                        else result.failureReason ?: result.errorCode ?: "Failed".localized()
                    }
                }
                tableCell(result.statusCode?.toString() ?: "-", 1, 14.px)
                tableCell(result.providerMessageId ?: "-", 3, 14.px)
                tableCell(result.detailsText(), 2, 14.px)
            }
            if (index != results.lastIndex) {
                verticalSeparator()
            }
        }
    }

    private fun SendPushPayloadEndpoint.SendResultDto.detailsText(): String {
        val invalidatedOn = tokenInvalidatedOn
        if (invalidatedOn != null) return "${"Token invalid since".localized()} $invalidatedOn"
        if (isSent.not()) return errorCode ?: "-"
        return "-"
    }

    private fun LinearLayout.historySection() = verticalLayout {
        style {
            width = matchParent
            height = wrapContent
        }

        horizontalLayout {
            style {
                width = matchParent
                alignItems = Alignment.Center
            }
            theme.subTitleFit(this, "Recent sends".localized())
            horizontalFiller()
            theme.unimportantButton(this) {
                text = "Refresh".localized()
                onClick = { viewModel.getHistory() }
            }
        }
        verticalFiller(8)

        verticalLayout {
            matchParentWidth()
            withLoadingAndError(viewModel.getHistoryUiState,
                onRetryClicked = { viewModel.getHistory() },
                onLoaded = { onHistoryLoaded() })
            historyContainer = verticalLayout {
                matchParentWidth()
            }
        }
    }

    private fun onHistoryLoaded() {
        historyContainer?.clearAllChildren()
        historyContainer?.apply {
            if (viewModel.history.isEmpty()) {
                theme.text(this) { text = "No sends recorded yet".localized() }
                return@apply
            }
            viewModel.history.forEach { send -> historyEntry(send) }
        }
    }

    private fun LinearLayout.historyEntry(send: GetPushSendHistoryEndpoint.PushSendDto) = verticalLayout {
        style {
            width = matchParent
            height = wrapContent
            padding = 12.px
            marginBottom = 12.px
            border = "1px solid ${AppColors.borderColor}"
            borderRadius = 4.px
        }

        horizontalLayout {
            style {
                width = matchParent
                alignItems = Alignment.Center
            }
            theme.text(this) {
                text = "${send.sentOn} - ${send.sentBy}"
                style { fontWeight = "bold" }
            }
            horizontalFiller()
            theme.text(this) {
                text = "${"Accepted".localized()}: ${send.sentCount}  ${"Failed".localized()}: ${send.failedCount}"
                style {
                    fontSize = 13.px
                    color = if (send.failedCount == 0) AppColors.greenDark else AppColors.redLight
                }
            }
            horizontalFiller(12)
            theme.text(this) {
                text = "${send.deliveryType} / ${send.priority}"
                style {
                    fontSize = 13.px
                    color = AppColors.textLight
                }
            }
            horizontalFiller(12)
            theme.text(this) {
                text = if (send.useSandbox) "Sandbox".localized() else "Production".localized()
                style {
                    fontSize = 13.px
                    color = AppColors.textLight
                }
            }
        }
        verticalFiller(8)

        theme.label(this, "Payload".localized(), false)
        theme.text(this) {
            text = send.payloadJson
            style {
                width = matchParent
                fontSize = 12.px
                whiteSpace = "pre-wrap"
                color = AppColors.textLight
            }
        }
        verticalFiller(8)
        resultsTable(send.results.toList())
    }
}
